import React, { useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Html5Qrcode } from 'html5-qrcode';
import Base from '../Base';
import Title from '../../components/common/Title';
import Back from '../../components/common/Back';
import Card from '../../components/elements/Card';
import Button from '../../components/elements/Button';
import Image from '../../components/images/Image';
import Icon from '../../components/common/Icon';
import { apiRequest } from '../../utils';
import { useTranslation } from 'react-i18next';

const SCANNER_ELEMENT_ID = 'crew-scan-reader';

const Page = () => {
  const { t } = useTranslation();
  // ?event=<id> — somebody scanning a NAMED event rather than the one their token sits on. Crew
  // never pass it: their token already sits on the event. Two kinds of people do.
  //
  // The HOST of a company event, scanning their own evening — the server recognises them.
  //
  // And whoever the organiser handed the scan link to: ?k=<key> is the secret out of that link,
  // and it is what the server checks instead of the person. That is the whole point of it — the
  // people at a Bravos door are receptionists and volunteers, not hosts, and nobody was ever
  // going to create a host row for each of them. Without a key an arbitrary id in the URL still
  // reaches nothing.
  const [params] = useSearchParams();
  const hostEventId = params.get('event');
  const scanKey = params.get('k');
  const scannerRef = useRef(null);
  const isScanningRef = useRef(false);
  const [scanning, setScanning] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  const stopScanner = async () => {
    const scanner = scannerRef.current;
    if (!scanner || !isScanningRef.current) return;
    try {
      await scanner.stop();
    } catch {
      /* already stopped */
    }
    try {
      await scanner.clear();
    } catch {
      /* ignore */
    }
    isScanningRef.current = false;
    setScanning(false);
  };

  const handleScanResult = async (decodedText) => {
    if (!decodedText) return;
    await stopScanner();
    setError(null);
    try {
      const res = await apiRequest('POST', '/check-in/scan',
        hostEventId
          ? { token: decodedText, event_id: hostEventId, ...(scanKey ? { scan_key: scanKey } : {}) }
          : { token: decodedText });
      if (res?.ticket) {
        // A perk ticket's redemption QR: the scan consumed it (or it already was).
        setResult({
          already: !!res.already_used,
          ticket: res.ticket,
          player: null,
          team: null,
        });
      } else {
        setResult({
          already: !!res?.already_checked_in,
          player: res?.player || null,
          team: res?.team || null,
        });
      }
    } catch (err) {
      setError(err.message || 'invalidQrCode');
      setResult(null);
    }
  };

  const startScanner = async () => {
    setError(null);
    setResult(null);

    if (!scannerRef.current) {
      scannerRef.current = new Html5Qrcode(SCANNER_ELEMENT_ID);
    }

    try {
      await scannerRef.current.start(
        { facingMode: 'environment' },
        { fps: 10, qrbox: { width: 240, height: 240 } },
        handleScanResult,
        () => { /* swallow per-frame decode errors */ },
      );
      isScanningRef.current = true;
      setScanning(true);
    } catch (err) {
      setError(err?.message || 'cameraAccessFailed');
    }
  };

  useEffect(() => {
    startScanner();
    return () => {
      stopScanner();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const resetForNextScan = async () => {
    setResult(null);
    setError(null);
    await startScanner();
  };

  return (
    <Base activeNav="home">
      <Back to={hostEventId && !scanKey ? `/host-event/${hostEventId}` : '/home'} />
      <Title>
        <Icon name="qrcode" theme="primary" className="me-2" style={{ fontSize: '1.4rem' }} />
        {t('scanCheckIn')}
      </Title>

      <Card className="crew-scan">
        <div
          id={SCANNER_ELEMENT_ID}
          className={`crew-scan__reader ${scanning ? '' : 'crew-scan__reader--idle'}`}
        />
        {!scanning && !result && !error && (
          <div className="crew-scan__placeholder">{t('startingCamera')}</div>
        )}
        {scanning && (
          <div className="crew-scan__hint">
            <Icon name="info" theme="primary" className="me-1" />
            {t('pointAtQr')}
          </div>
        )}
      </Card>

      {result && (
        <Card
          className={`scan-result ${result.already ? 'scan-result--already' : 'scan-result--ok'}`}
        >
          <div className="scan-result__icon">
            <Icon
              name={result.already ? 'info' : 'check-circle'}
              theme="light"
            />
          </div>

          <div className="scan-result__title">
            {result.ticket
              ? (result.already ? t('ticketAlreadyUsed') : t('ticketRedeemed'))
              : (result.already ? t('alreadyCheckedIn') : t('checkedIn'))}
          </div>

          {result.ticket && (
            <div className="scan-result__player">
              <div className="scan-result__player-name">
                {result.ticket.emoji ? `${result.ticket.emoji} ` : ''}
                {result.ticket.type_name || t('ticket')}
              </div>
              {result.ticket.holder_name && (
                <div className="scan-result__team">{result.ticket.holder_name}</div>
              )}
              <div className="scan-result__team">
                {result.ticket.code}
                {result.ticket.payment_status === 'unpaid' && (
                  <span className="badge bg-warning-subtle text-warning-emphasis ms-2">
                    {t('unpaid')}
                  </span>
                )}
              </div>
            </div>
          )}

          {result.player && (
            <div className="scan-result__player">
              {result.player.photo_url && (
                <Image
                  url={result.player.photo_url}
                  className="circle scan-result__avatar"
                  size="4.5rem"
                />
              )}
              <div className="scan-result__player-name">
                {result.player.full_name ||
                  `${result.player.first_name || ''} ${result.player.name || ''}`.trim()}
                {result.player.is_captain && (
                  <Icon name="user-crown" theme="primary" className="ms-1" />
                )}
              </div>
              {result.team && (
                <div className="scan-result__team">
                  {result.team.house_color && (
                    <Icon
                      name="flag"
                      style={{ color: result.team.house_color, fontSize: '0.75rem' }}
                      className="me-1"
                      theme=""
                    />
                  )}
                  {result.team.name}
                </div>
              )}
            </div>
          )}

          <Button onClick={resetForNextScan} mb="0">
            <Icon name="qrcode" theme="light" className="me-2" />
            {t('scanAnother')}
          </Button>
        </Card>
      )}

      {error && (
        <Card className="scan-result scan-result--error">
          <div className="scan-result__icon scan-result__icon--error">
            <Icon name="cross-circle" theme="light" />
          </div>
          <div className="scan-result__title">{t(error, { defaultValue: error })}</div>
          <Button onClick={resetForNextScan} mb="0">
            <Icon name="qrcode" theme="light" className="me-2" />
            {t('tryAgain')}
          </Button>
        </Card>
      )}
    </Base>
  );
};

export default Page;
